/* eslint-disable react/no-array-index-key */
import { useMutation } from '@apollo/client';
import { Drawer, notification, Tabs } from 'antd';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import React, { useEffect, useRef, useState } from 'react';
import { CREATE_TRANSACTION_TYPE, UPDATE_TRANSACTION_TYPE } from 'services/graphql/mutations/transactionTypes';

import { ITransactionTypeDrawer } from '../types';
import TransactionTypeForm from './TransactionTypeForm';
import CompanySpecificPrice from './TransactionTypeSpecificPrices/CompanySpecificPrice';
import NotarySpecificPrice from './TransactionTypeSpecificPrices/NotarySpecificPrice';
import { ITransactionTypesState } from './types';

const { TabPane } = Tabs;

const TransactionTypeDrawer: React.FC<ITransactionTypeDrawer> = ({
  activeTransactionType,
  activeService,
  closeDrawer,
  refetch,
  drawerVisible,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const drawerTabs = [
    {
      title: 'General Information',
      component: TransactionTypeForm,
    },
    {
      title: 'Specific Notary Prices',
      component: NotarySpecificPrice,
    },
    {
      title: 'Specific Company Prices',
      component: CompanySpecificPrice,
    },
  ];

  const [transactionTypeData, setTransactionTypeData] = useState<ITransactionTypesState>({
    general: undefined,
    userSpecific: undefined,
    companySpecific: undefined,
  });

  const detailTabRef = useRef<any>(null);
  const userSpecificPriceTabRef = useRef<any>(null);
  const companySpecificPriceTabRef = useRef<any>(null);

  // const [transactionSpecificPriceData, setTransactionSpecificPriceData] = useState({});
  const onSaveClick = () => {
    detailTabRef.current?.handleSubmit?.();
    userSpecificPriceTabRef.current?.handleSubmit?.();
    companySpecificPriceTabRef.current?.handleSubmit?.();
  };

  const displayNotification = (isSuccess: boolean, isCreate: boolean) => {
    if (isSuccess) {
      notification.success({ message: `${isCreate ? 'Create' : 'Edit'} Transaction type successfully!`, duration: 2 });
    } else {
      notification.error({ message: `${isCreate ? 'Create' : 'Edit'} Transaction Error! Try again.`, duration: 2 });
    }
  };

  const [updateTransactionType] = useMutation(UPDATE_TRANSACTION_TYPE);
  const [createTransactionType] = useMutation(CREATE_TRANSACTION_TYPE);

  const callCreateTransactionType = async () => {
    const generalTransactionTypeData: any = transactionTypeData.general || {};
    const userSpecificTransactionTypePrices: any = transactionTypeData.userSpecific || {};
    const companySpecificTransactionTypePrices: any = transactionTypeData.companySpecific || {};
    const newTransactionTypeValues = {
      serviceType: activeService?.id,
      name: generalTransactionTypeData?.name,
      description: generalTransactionTypeData?.description,
      defaultPriceInput: {
        clientRONFee: parseFloat(generalTransactionTypeData?.clientRONFee || ''),
        clientInPersonFee: parseFloat(generalTransactionTypeData?.clientInPersonFee || ''),
        notaryGetInPerson: parseFloat(generalTransactionTypeData?.notaryGetInPerson || ''),
        notaryGetRON: parseFloat(generalTransactionTypeData?.notaryGetRON || ''),
      },
      // ... add more field for specific prices
      userPriceInputs: userSpecificTransactionTypePrices?.user?.map((u: string, index: number) => ({
        notary: u,
        priceRON: parseFloat(userSpecificTransactionTypePrices.priceRON?.[index] || '0'),
        priceInPerson: parseFloat(userSpecificTransactionTypePrices.priceInPerson?.[index] || '0'),
      })),
      companyPriceInputs: companySpecificTransactionTypePrices?.company?.map((c: string, index: number) => ({
        company: c,
        priceRON: parseFloat(companySpecificTransactionTypePrices.priceRON?.[index] || '0'),
        priceInPerson: parseFloat(companySpecificTransactionTypePrices.priceInPerson?.[index] || '0'),
      })),
      configs: {
        RON: { duration: (generalTransactionTypeData.durationRON || 0) * 3600 },
        IN_PERSON: { duration: (generalTransactionTypeData.durationInPerson || 0) * 3600 },
      },
    };

    try {
      await createTransactionType({
        variables: {
          doc: newTransactionTypeValues,
        },
      });
      setTransactionTypeData({ general: undefined, userSpecific: undefined, companySpecific: undefined });
      displayNotification(true, true);
      refetch?.();
      setSubmitLoading(false);
      closeDrawer?.();
    } catch (err) {
      console.log('ERROR', err);
      setSubmitLoading(false);
      displayNotification(false, true);
    }
  };

  const callUpdateTransactionType = async () => {
    const generalTransactionTypeData: any = transactionTypeData.general || {};
    const userSpecificTransactionTypePrices: any = transactionTypeData.userSpecific || {};
    const companySpecificTransactionTypePrices: any = transactionTypeData.companySpecific || {};

    const userPriceInputs: any[] = userSpecificTransactionTypePrices.user?.map((u: string, index: number) => ({
      notary: u,
      priceInPerson: parseFloat(userSpecificTransactionTypePrices.priceInPerson[index] || '0'),
      priceRON: parseFloat(userSpecificTransactionTypePrices.priceRON[index] || '0'),
      transactionType: activeTransactionType?.id,
      id: activeTransactionType?.allUserPrice[index]?.id,
    }));

    const companyPriceInputs: any[] = companySpecificTransactionTypePrices.company?.map((c: string, index: number) => ({
      company: c,
      priceInPerson: parseFloat(companySpecificTransactionTypePrices.priceInPerson[index] || '0'),
      priceRON: parseFloat(companySpecificTransactionTypePrices.priceRON[index] || '0'),
      transactionType: activeTransactionType?.id,
      id: activeTransactionType?.allUserPrice[index]?.id,
    }));

    const newTransactionTypeValues = {
      serviceType: activeService?.id,
      name: generalTransactionTypeData?.name,
      description: generalTransactionTypeData?.description,
      defaultPriceInput: {
        clientRONFee: parseFloat(generalTransactionTypeData?.clientRONFee || ''),
        clientInPersonFee: parseFloat(generalTransactionTypeData?.clientInPersonFee || ''),
        notaryGetInPerson: parseFloat(generalTransactionTypeData?.notaryGetInPerson || ''),
        notaryGetRON: parseFloat(generalTransactionTypeData?.notaryGetRON || ''),
      },
      userPriceInputs,
      companyPriceInputs,
      configs: {
        RON: { duration: (generalTransactionTypeData.durationRON || 0) * 3600 },
        IN_PERSON: { duration: (generalTransactionTypeData.durationInPerson || 0) * 3600 },
      },
    };

    try {
      // edit
      await updateTransactionType({
        variables: {
          updatedDoc: newTransactionTypeValues,
          filter: {
            id: activeTransactionType?.id,
          },
        },
      });
      refetch?.();
      displayNotification(true, false);
      setSubmitLoading(false);
      closeDrawer?.();
    } catch (err) {
      console.log('ERROR', err);
      setSubmitLoading(false);
      displayNotification(false, false);
    }
  };

  useEffect(() => {
    if (Object.values(transactionTypeData).every((data) => data !== undefined)) {
      setSubmitLoading(true);
      if (!activeTransactionType) callCreateTransactionType();
      else {
        callUpdateTransactionType();
      }
    }
  }, [transactionTypeData]);

  const onSubmit = (v: any) => {
    // if (!activeTransactionType) {
    setTransactionTypeData((c) => {
      let newData: Record<string, any> = {};
      if (v.description) {
        newData = { general: v };
      } else if (v.user) {
        newData = { userSpecific: v };
      } else {
        newData = { companySpecific: v };
      }
      if (userSpecificPriceTabRef.current === null) {
        newData.userSpecific = null;
      }
      if (companySpecificPriceTabRef.current === null) {
        newData.companySpecific = null;
      }
      return { ...c, ...newData };
    });
  };

  return (
    <Drawer
      className='transaction-type-drawer'
      title={
        <div className='gx-flex-row gx-flex-nowrap'>
          <div className='gx-flex-column'>
            <span className='ant-drawer-title'>{activeTransactionType?.name || 'New Transaction Type'}</span>
            <span className='gx-fs-md gx-font-weight-normal gx-mt-1'>
              {activeTransactionType?.description || `Service: ${activeService?.name}`}
            </span>
          </div>
          <div style={{ flex: 1, textAlign: 'end' }}>
            <ButtonWithSpinner
              width='70px'
              buttonType='primary'
              onClick={onSaveClick}
              className='gx-m-0'
              isLoading={submitLoading}>
              Save
            </ButtonWithSpinner>
          </div>
        </div>
      }
      maskClosable={false}
      bodyStyle={{ padding: '0 20px' }}
      onClose={() => {
        closeDrawer?.();
        setTransactionTypeData({ general: undefined, userSpecific: undefined, companySpecific: undefined });
      }}
      visible={drawerVisible}
      destroyOnClose>
      <Tabs defaultActiveKey='0'>
        {drawerTabs.map((tab, index) => {
          let drawerTabRef: any;
          switch (index) {
            case 0:
              drawerTabRef = detailTabRef;
              break;
            case 1:
              drawerTabRef = userSpecificPriceTabRef;
              break;
            case 2:
              drawerTabRef = companySpecificPriceTabRef;
              break;
            default:
              break;
          }
          return (
            <TabPane tab={tab.title} key={`${index}`}>
              <tab.component
                formRef={drawerTabRef}
                onSubmit={onSubmit}
                activeTransactionType={activeTransactionType}
                activeService={activeService}
              />
            </TabPane>
          );
        })}
      </Tabs>
    </Drawer>
  );
};

export default React.memo(TransactionTypeDrawer);
